import { useEffect, useState } from 'react';
import {
  BucketCounts, KeyType, Scope, SummaryResponse, WindowKey, executionStatsService, isAbort
} from '../../services/executionStatsService';

/**
 * Badge data, batched: one request per page, not one per badge.
 *
 * A project page with twelve workflow cards, or a canvas with twenty nodes,
 * mounts a badge per item. Each registers its key here; the batcher waits a
 * tick, sends one /summary for every key registered in it, and hands each
 * badge its slice. Results are cached for a minute — the API caches for the
 * same time, so a sooner refetch would only return the same numbers.
 */

const BATCH_DELAY_MS = 50;
const CACHE_TTL_MS = 60 * 1000;
const MAX_KEYS_PER_REQUEST = 200;

export interface SummaryDescriptor {
  scope: Scope;
  keyType: KeyType;
  key: string;
  groupVersions?: boolean;
}

export interface KeySummary {
  counts: Partial<Record<WindowKey, BucketCounts>>;
  staleAfterHours: number;
  tracked: SummaryResponse['tracked'];
  runtimeStatusSince: string | null;
  /** Campaigns only. */
  lastRunAt?: string | null;
}

type Listener = (result: { data?: KeySummary; error?: Error }) => void;

interface Batch {
  scope: Scope;
  keyType: KeyType;
  groupVersions: boolean;
  listeners: Map<string, Set<Listener>>;
  timer: ReturnType<typeof setTimeout> | null;
  controller: AbortController | null;
}

const VERSION_SUFFIX = /_\d{8}T\d{6}$/;

/** The key the API reports under — version-stripped when versions are grouped. */
export const reportedKey = ({ keyType, key, groupVersions }: SummaryDescriptor): string =>
  keyType === 'code' && groupVersions ? key.replace(VERSION_SUFFIX, '') : key;

const groupId = ({ scope, keyType, groupVersions }: Omit<SummaryDescriptor, 'key'>) =>
  `${scope}:${keyType}:${groupVersions ? 'v' : '-'}`;

const cacheId = (d: SummaryDescriptor) => `${groupId(d)}:${reportedKey(d)}`;

const cache = new Map<string, { expiresAt: number; value: KeySummary }>();
const pending = new Map<string, Batch>();

function readCache(id: string): KeySummary | undefined {
  const hit = cache.get(id);
  if (!hit) return undefined;
  if (hit.expiresAt <= Date.now()) {
    cache.delete(id);
    return undefined;
  }
  return hit.value;
}

function flush(id: string) {
  const batch = pending.get(id);
  if (!batch) return;
  pending.delete(id);
  batch.timer = null;
  const keys = [...batch.listeners.keys()];
  if (!keys.length) return;

  const controller = new AbortController();
  batch.controller = controller;

  // The API takes up to 200 keys; a larger page is split rather than refused.
  for (let i = 0; i < keys.length; i += MAX_KEYS_PER_REQUEST) {
    const slice = keys.slice(i, i + MAX_KEYS_PER_REQUEST);
    executionStatsService
      .getSummary(
        { scope: batch.scope, keyType: batch.keyType, keys: slice, groupVersions: batch.groupVersions },
        { signal: controller.signal }
      )
      .then((res) => {
        for (const key of slice) {
          const value: KeySummary = {
            counts: res.stats?.[key] ?? {},
            staleAfterHours: res.staleAfterHours,
            tracked: res.tracked,
            runtimeStatusSince: res.runtimeStatusSince ?? null,
            ...(res.meta ? { lastRunAt: res.meta[key]?.lastRunAt ?? null } : {})
          };
          cache.set(`${id}:${key}`, { expiresAt: Date.now() + CACHE_TTL_MS, value });
          batch.listeners.get(key)?.forEach((listener) => listener({ data: value }));
        }
      })
      .catch((error) => {
        if (isAbort(error)) return;
        for (const key of slice) batch.listeners.get(key)?.forEach((listener) => listener({ error }));
      });
  }
}

/**
 * Registers a listener for one key. Returns the unsubscribe; when the last
 * listener of an in-flight batch leaves, the request is aborted.
 */
export function subscribeSummary(descriptor: SummaryDescriptor, listener: Listener): () => void {
  const id = groupId(descriptor);
  const key = reportedKey(descriptor);

  let batch = pending.get(id);
  if (!batch) {
    batch = {
      scope: descriptor.scope,
      keyType: descriptor.keyType,
      groupVersions: Boolean(descriptor.groupVersions),
      listeners: new Map(),
      timer: null,
      controller: null
    };
    pending.set(id, batch);
    const created = batch;
    created.timer = setTimeout(() => flush(id), BATCH_DELAY_MS);
  }
  const target = batch;
  if (!target.listeners.has(key)) target.listeners.set(key, new Set());
  target.listeners.get(key)!.add(listener);

  return () => {
    const set = target.listeners.get(key);
    set?.delete(listener);
    if (set && set.size === 0) target.listeners.delete(key);
    if (target.listeners.size > 0) return;
    if (target.timer) {
      clearTimeout(target.timer);
      target.timer = null;
      if (pending.get(id) === target) pending.delete(id);
    }
    target.controller?.abort();
  };
}

/** Test seam: forget cached results and pending batches. */
export function resetExecutionSummaryCache() {
  cache.clear();
  pending.forEach((batch) => batch.timer && clearTimeout(batch.timer));
  pending.clear();
}

export interface SummaryState {
  data: KeySummary | null;
  loading: boolean;
  error: string | null;
}

/**
 * One key's rolling 24h / 7d / 30d counts. Pass `key: null` to skip, which
 * keeps the hook callable unconditionally.
 */
export function useExecutionSummary(descriptor: SummaryDescriptor | { key: null }): SummaryState {
  const active = descriptor.key !== null ? (descriptor as SummaryDescriptor) : null;
  const id = active ? cacheId(active) : null;

  const [state, setState] = useState<SummaryState>(() => {
    const hit = id ? readCache(id) : undefined;
    return { data: hit ?? null, loading: Boolean(active) && !hit, error: null };
  });

  useEffect(() => {
    if (!active || !id) {
      setState({ data: null, loading: false, error: null });
      return;
    }
    const hit = readCache(id);
    if (hit) {
      setState({ data: hit, loading: false, error: null });
      return;
    }
    setState((prev) => ({ data: prev.data, loading: true, error: null }));
    return subscribeSummary(active, ({ data, error }) => {
      if (data) setState({ data, loading: false, error: null });
      else setState({ data: null, loading: false, error: error?.message || 'Could not load run statistics.' });
    });
    // `id` captures every field of the descriptor.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  return state;
}

export interface WorkflowRunStats extends SummaryState {
  /**
   * 'campaign' — this campaign's own runs of its flow.
   * 'flow' — every run of the flow, because none of this campaign's runs are
   * tagged with it; the badge says so.
   */
  attribution: 'campaign' | 'flow' | null;
}

/**
 * A workflow card's numbers: the campaign's own runs, or the flow's when the
 * campaign has never had a tagged run.
 */
export function useWorkflowRunStats({
  campaignId,
  flowCode
}: {
  campaignId?: string | null;
  flowCode?: string | null;
}): WorkflowRunStats {
  const campaign = useExecutionSummary(
    campaignId ? { scope: 'flow', keyType: 'campaign', key: campaignId } : { key: null }
  );

  const untagged = !campaignId || (campaign.data !== null && !campaign.data.lastRunAt);
  const flow = useExecutionSummary(
    untagged && flowCode
      ? { scope: 'flow', keyType: 'code', key: flowCode, groupVersions: true }
      : { key: null }
  );

  if (campaignId && (campaign.loading || campaign.error || !untagged)) {
    return { ...campaign, attribution: campaign.data ? 'campaign' : null };
  }
  if (flowCode) {
    return { ...flow, attribution: flow.data ? 'flow' : null };
  }
  // A campaign with no run and no flow to fall back to: its own zeros are the answer.
  return { ...campaign, attribution: campaign.data ? 'campaign' : null };
}
